import { useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import BookConsultationDialog from "@/components/site/BookConsultationDialog";
import JoinUsDialog from "@/components/site/JoinUsDialog";
import FloatingActions from "@/components/site/FloatingActions";
import EditorialHero from "@/components/site/v2/EditorialHero";
import ChallengeCards from "@/components/site/v2/ChallengeCards";
import ServiceExplorer from "@/components/site/v2/ServiceExplorer";
import FrameworkDiagram from "@/components/site/v2/FrameworkDiagram";
import OutcomeStrip from "@/components/site/v2/OutcomeStrip";
import ImpactStrip from "@/components/site/v2/ImpactStrip";
import FaqSection from "@/components/site/v2/FaqSection";
import RelatedLinks from "@/components/site/v2/RelatedLinks";
import ContextualCTA from "@/components/site/v2/ContextualCTA";
import ImageBand from "@/components/site/v2/ImageBand";
import LeadMagnetSection from "@/components/site/v2/LeadMagnetSection";
import DiagnosticOffer from "@/components/site/v2/DiagnosticOffer";
import LeadMagnetDialog from "@/components/site/LeadMagnetDialog";
import { SERVICE_LEAD_MAGNET } from "@/data/leadMagnets";
import ToolPromo from "@/components/tools/ToolPromo";
import RelatedInsights from "@/components/site/v2/RelatedInsights";
import { SERVICE_INSIGHTS } from "@/data/insightsMap";
import { STRATEGY_SUBPAGES } from "@/data/strategySubPages";
import { INTELLIGENCE_SUBPAGES } from "@/data/intelligenceSubPages";
import { BRAND_SUBPAGES } from "@/data/brandSubPages";
import { DIGITAL_SUBPAGES } from "@/data/digitalSubPages";
import { SERVICE_V2 } from "@/data/v2Content";
import { serviceImagePool, imageForPath, uniquePicker, ALL_IMAGES } from "@/data/images";
import { SERVICE_SEO } from "@/data/seoMap";
import { SERVICE_SOLUTION, getSolution } from "@/data/solutions";
import { track } from "@/lib/analytics";
import { usePageMeta } from "@/lib/usePageMeta";

const CAPABILITIES = {
  "strategy-commercial-transformation": {
    name: "Strategy & Commercial Transformation",
    subpages: STRATEGY_SUBPAGES,
  },
  "market-customer-intelligence": {
    name: "Market & Customer Intelligence",
    subpages: INTELLIGENCE_SUBPAGES,
  },
  "brand-marketing-communications": {
    name: "Brand, Marketing & Communications",
    subpages: BRAND_SUBPAGES,
  },
  "digital-commerce": {
    name: "Digital & Commerce",
    subpages: DIGITAL_SUBPAGES,
  },
};

const BAND_IMAGES = {
  "strategy-commercial-transformation": "/photos/band-strategy.jpg",
  "market-customer-intelligence": "/photos/band-intelligence.jpg",
  "brand-marketing-communications": "/photos/band-brand.jpg",
  "digital-commerce": "/photos/band-digital.jpg",
};

// Priority services carry the 30-minute Growth Diagnostic offer (approved).
const PRIORITY_SERVICES = new Set([
  "sales-transformation",
  "go-to-market-market-entry",
  "channel-route-to-market",
  "pricing-revenue-management",
  "marketing-strategy-integrated-communications",
  "performance-marketing-paid-media",
  "ecommerce-marketplace-growth",
  "brand-strategy-positioning",
]);

/**
 * Per-card deep links for pages whose solution modules previously all
 * pointed at the practice hub with identical "Explore …" text.
 * A module set to null renders without a link (no relevant sub-page exists).
 */
const MODULE_LINKS = {
  "ecommerce-marketplace-growth": {
    "Amazon Growth": {
      to: "/capabilities/digital-commerce/amazon-marketplace-growth",
      label: "Explore Amazon Marketplace Growth",
    },
  },
  "amazon-marketplace-growth": {
    "Marketplace Strategy": { to: "/capabilities/digital-commerce/ecommerce-marketplace-growth", label: "Explore E-Commerce & Marketplace Growth" },
    "Product Listing Optimisation": null,
    "Amazon Advertising": { to: "/capabilities/digital-commerce/performance-marketing-paid-media", label: "Explore Performance Marketing & Paid Media" },
    "Portfolio & Pricing": { to: "/capabilities/strategy-commercial-transformation/pricing-revenue-management", label: "Explore Pricing & Revenue Management" },
    "Conversion Improvement": { to: "/capabilities/digital-commerce/d2c-cro-digital-experience", label: "Explore D2C, CRO & Digital Experience" },
    "Promotional Planning": { to: "/capabilities/strategy-commercial-transformation/pricing-revenue-management", label: "Explore Pricing & Revenue Management" },
    "Marketplace Analytics": null,
    "Account Management": null,
  },
  "d2c-performance-marketing": {
    "Paid Acquisition Strategy": { to: "/capabilities/digital-commerce/performance-marketing-paid-media", label: "Explore Performance Marketing & Paid Media" },
    "Campaign Management": { to: "/capabilities/digital-commerce/performance-marketing-paid-media", label: "Explore Performance Marketing & Paid Media" },
    "Creative Testing System": { to: "/capabilities/brand-marketing-communications/advertising-content-creative", label: "Explore Advertising, Content & Creative" },
    "Conversion Rate Optimisation": { to: "/capabilities/digital-commerce/d2c-cro-digital-experience", label: "Explore D2C, CRO & Digital Experience" },
    "Attribution & Analytics": null,
    "Retargeting & CRM": { to: "/capabilities/digital-commerce/crm-retention-loyalty", label: "Explore CRM, Retention & Loyalty" },
    "CAC & Profitability Management": null,
  },
};

// Pages where an unmapped module must render WITHOUT a link (rather than
// falling back to the generic hub link).
const MODULE_LINKS_STRICT = new Set(["amazon-marketplace-growth", "d2c-performance-marketing"]);

/**
 * V2 service detail template — all 25 service sub-pages.
 * Concise hero → business problems → expandable solution modules →
 * framework → outcomes → leadership experience → related → FAQ → CTA.
 * Priority pages use hand-curated problems/modules from SERVICE_V2;
 * the rest derive the same structure from their existing page data.
 */
export default function ServiceDetailPage() {
  const { slug, subSlug } = useParams();
  const [bookOpen, setBookOpen] = useState(false);
  const [magnetOpen, setMagnetOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  const cap = CAPABILITIES[slug];
  const page = cap?.subpages.find((p) => p.slug === subSlug);
  const seo = SERVICE_SEO[subSlug] || {};

  const capUrl = `/capabilities/${slug}`;

  usePageMeta({
    title: page ? seo.metaTitle || `${page.title} — ${cap.name} | BLEZZINGS` : "Our Services | BLEZZINGS",
    description: page?.metaDescription || "Explore BLEZZINGS consulting services.",
    canonical: page ? `https://blezzings.co${capUrl}/${page.slug}` : undefined,
    breadcrumbs: page
      ? [
          { name: "Home", url: "https://blezzings.co/" },
          { name: "Our Services", url: `https://blezzings.co${capUrl}` },
          { name: cap.name, url: `https://blezzings.co${capUrl}` },
          { name: page.title, url: `https://blezzings.co${capUrl}/${page.slug}` },
        ]
      : undefined,
    faqs: page?.faqs,
    service: page
      ? {
          name: page.title,
          description: page.metaDescription,
          url: `https://blezzings.co${capUrl}/${page.slug}`,
        }
      : undefined,
  });

  if (!page) return <Navigate to="/" replace />;

  const openBook = (source) => {
    track("book_consultation_open", { source });
    track("service_cta_click", {
      service: page.title,
      source_page: window.location.pathname,
      cta_location: source,
    });
    setBookOpen(true);
  };

  // Curated content for priority pages; generic mapping otherwise.
  const v2 = SERVICE_V2[subSlug];

  // Business problems — curated cards where available, otherwise the
  // page's own symptom/challenge lists (6 cards + the rest as chips).
  const symptoms =
    page.challengeSymptoms ||
    (page.challengeBlocks || [])
      .filter((b) => b.type === "list")
      .flatMap((b) => b.items);
  const challengeIntro =
    !page.challengeSymptoms &&
    (page.challengeBlocks || []).find((b) => b.type === "paras")?.items?.join(" ");
  // No image repeats on the page: hero + band are reserved up front, and every
  // card image is picked uniquely (most relevant first, spare pool as fallback).
  const pick = uniquePicker(
    [page.image, BAND_IMAGES[slug]],
    [serviceImagePool(slug, page.image), ALL_IMAGES]
  );
  // Challenge cards use custom per-card artwork (unique by construction).
  const challenges = (v2?.problems || symptoms.slice(0, 6).map((s) => ({ title: s }))).map(
    (c, i) => ({ ...c, image: `/photos/svc-challenge-${subSlug}-${i + 1}.jpg` })
  );
  const extraSymptoms = v2 ? [] : symptoms.slice(6);

  // Solution modules — curated where available, otherwise the page's own
  // transformation modules. Cards link to the most relevant sub-page where
  // one exists; on strict pages a card without a relevant target shows none.
  const moduleLinks = MODULE_LINKS[subSlug];
  const modules = (
    v2?.modules ||
    page.transform.map((t) => {
      let link = { to: capUrl, label: `Explore ${cap.name}` };
      if (moduleLinks) {
        const override = moduleLinks[t.name];
        if (override !== undefined) link = override;
        else if (MODULE_LINKS_STRICT.has(subSlug)) link = null;
      }
      return { name: t.name, body: t.body, link };
    })
  ).map((m) => ({
    ...m,
    image: m.image || pick(imageForPath(m.link?.to || capUrl)),
  }));

  return (
    <div data-testid="service-detail-page" className="relative">
      <Nav onBook={() => openBook("nav")} />

      <EditorialHero
        testid="service-hero"
        eyebrow={page.title}
        title={seo.heroH1 || page.title}
        description={page.heroParas[1] || page.heroParas[0]}
        tagline={page.heroSubhead}
        image={page.image}
        imageAlt={page.imageAlt}
        primaryLabel={seo.heroCta || "Book a Consultation"}
        onPrimary={() => openBook("service-hero")}
        secondaryLabel="Explore the Solutions"
        secondaryTo="#solutions"
      />

      {subSlug === "ecommerce-marketplace-growth" && (
        <div className="bg-[#fafafa] border-b border-black/10">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-5">
            <p data-testid="ecom-amazon-crosslink-note" className="text-sm text-zinc-600">
              Looking for Amazon-specific account management? See our{" "}
              <Link
                to="/capabilities/digital-commerce/amazon-marketplace-growth"
                data-testid="ecom-amazon-crosslink"
                className="font-medium text-black underline underline-offset-4 hover:text-zinc-600 transition-colors"
              >
                Amazon Marketplace Growth &amp; Management
              </Link>{" "}
              service.
            </p>
          </div>
        </div>
      )}

      <ChallengeCards
        testid="service-problems"
        badge="The Challenge"
        headline={v2?.challengeLead || page.challengeLead}
        intro={challengeIntro || undefined}
        challenges={challenges}
        extra={extraSymptoms}
        kicker={page.challengeClosing}
      />

      <div id="solutions">
        <ServiceExplorer
          testid="service-modules"
          badge="What We Do"
          headline={page.perspectiveLead}
          items={modules}
          deliverablesLabel="What this can include"
          deliverables={v2 ? undefined : page.deliver}
        />
      </div>

      <FrameworkDiagram
        testid="service-framework"
        badge="How We Work"
        headline={v2?.frameworkHeadline || "From insight to impact."}
        steps={page.approach.map((s) => ({ name: s.title, desc: s.body }))}
        kicker={page.perspectiveHighlight}
      />

      <ImageBand
        testid="service-band"
        image={BAND_IMAGES[slug]}
        alt={`${cap.name} — BLEZZINGS`}
        caption={cap.name}
      />

      {SERVICE_LEAD_MAGNET[subSlug] && (
        <LeadMagnetSection
          testid="service-leadmagnet"
          resource={SERVICE_LEAD_MAGNET[subSlug]}
          onOpen={() => setMagnetOpen(true)}
        />
      )}

      <ToolPromo pageUrl={`${capUrl}/${subSlug}`} testid="service-tool-promo" />

      {/* Capability vs packaged solution — one distinguishing line (spec §14) */}
      {(() => {
        const sid = SERVICE_SOLUTION[`${slug}/${subSlug}`];
        if (!sid) return null;
        const s = getSolution(sid);
        return (
          <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 pb-4">
            <p className="text-sm text-zinc-500" data-testid="service-solution-line">
              This is the broad capability. For a focused, time-boxed engagement on exactly this problem, see{" "}
              <Link
                to={`/solutions/${sid}`}
                data-testid="service-solution-link"
                className="text-black font-medium underline underline-offset-4 decoration-zinc-300 hover:decoration-black transition-colors"
              >
                {s.name} — {s.descriptor}
              </Link>
              .
            </p>
          </div>
        );
      })()}

      <OutcomeStrip
        testid="service-outcomes"
        headline="What improves when the system works."
        items={page.outcomes.slice(0, 6)}
        note="Areas of commercial impact — the specific metrics and targets are agreed according to the engagement."
      />

      <ImpactStrip
        testid="service-impact"
        headline="Familiar territory for our leadership."
        impact={page.impact}
        note={page.impactNote}
      />

      {PRIORITY_SERVICES.has(subSlug) && (
        <DiagnosticOffer
          testid="service-diagnostic"
          onRequest={() => openBook("growth-diagnostic")}
        />
      )}

      <RelatedLinks
        testid="service-related"
        badge="Related Services"
        links={page.related.map((r) => ({ ...r, image: pick(imageForPath(r.to)) }))}
      />

      <FaqSection testid="service-faqs" faqs={page.faqs} />

      <RelatedInsights testid="service-insights" slugs={SERVICE_INSIGHTS[subSlug]} />

      <ContextualCTA
        testid="service-cta"
        heading={page.ctaHeading}
        body={page.ctaBody}
        label={page.ctaLabel}
        onBook={() => openBook("service-cta")}
      />

      <Footer onJoin={() => setJoinOpen(true)} />
      <FloatingActions onBook={() => openBook("floating")} />
      <BookConsultationDialog open={bookOpen} onOpenChange={setBookOpen} service={page.title} />
      <LeadMagnetDialog open={magnetOpen} onOpenChange={setMagnetOpen} resource={SERVICE_LEAD_MAGNET[subSlug]} />
      <JoinUsDialog open={joinOpen} onOpenChange={setJoinOpen} />
    </div>
  );
}
